import React from "react";
import { Box, Button, Typography, Stack, type SxProps, type Theme } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface DashboardHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBack?: () => void;
  containerSx?: SxProps<Theme>;
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: "contained" | "outlined";
    color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  };
  primaryActionSx?: SxProps<Theme>;
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: "contained" | "outlined";
    color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  };
  secondaryActionSx?: SxProps<Theme>;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  showBackButton = true,
  onBack,
  containerSx,
  primaryAction,
  primaryActionSx,
  secondaryAction,
  secondaryActionSx,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1); // Always use browser back functionality
  };

  return (
    <Box
      sx={{
        py: 5, // 40px top and bottom spacing
        px: 3, // 24px horizontal padding
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: { xs: "flex-start", sm: "space-between" },
        gap: { xs: 2, sm: 0 },
        width: "100%",
        marginLeft: "-24px", // Counteract parent padding
        marginRight: "-24px", // Counteract parent padding
        paddingLeft: "24px", // Restore horizontal padding
        paddingRight: "24px", // Restore horizontal padding
        ...(containerSx as Record<string, unknown>),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        {showBackButton && (
          <Button
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "text.secondary",
              borderColor: "text.secondary",
              "&:hover": {
                borderColor: "text.primary",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Back
          </Button>
        )}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontSize: "25px",
          }}
        >
          {title}
        </Typography>
      </Stack>

      {(primaryAction || secondaryAction) && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {secondaryAction && (
            <Button
              onClick={secondaryAction.onClick}
              variant={secondaryAction.variant || "outlined"}
              color={secondaryAction.color || "secondary"}
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                fontWeight: 600,
                width: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
                ...(secondaryActionSx as Record<string, unknown>),
              }}
            >
              {secondaryAction.label}
            </Button>
          )}

          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              variant={primaryAction.variant || "contained"}
              color={primaryAction.color || "primary"}
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                fontWeight: 600,
                width: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
                ...(primaryActionSx as Record<string, unknown>),
              }}
            >
              {primaryAction.label}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default DashboardHeader;
